import { useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Theme,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { makeStyles } from 'tss-react/mui';
import { AxiosError } from 'axios';

import FormSubmitButton from '../components/buttons/FormSubmitButton';
import FormCancelButton from '../components/buttons/FormCancelButton';
import useSnackBar from '../hooks/useSnackBar';
import { ErrorData } from '../types';

const useStyles = makeStyles()((theme: Theme) => ({
  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      maxWidth: '650px',
      width: '100%',
      padding: '32px',
      boxSizing: 'border-box',
      borderRadius: '16px',
    },

    '& .MuiDialogTitle-root': {
      padding: 0,
      fontSize: '28px',
      fontWeight: 600,
      lineHeight: '120%',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      marginBottom: '24px',
    },

    '& .MuiDialogContent-root': {
      padding: 0,
    },
  },
  formContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
  },
  popupActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: 0,
    marginTop: '32px',
  },
  headline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  uploadBox: {
    border: `1px dashed ${theme.palette.secondary.main}`,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    background: 'rgba(255,255,255,0.03)',
    cursor: 'pointer',
    transition: '0.2s ease',

    '&:hover': {
      background: 'rgba(255,255,255,0.06)',
    },
  },
  uploadIcon: {
    fontSize: '40px',
    color: theme.palette.info.main,
    marginBottom: '8px',
  },
  uploadText: {
    color: theme.palette.common.white,
    fontWeight: 500,
    fontSize: '16px',
    fontFamily: 'Inter',
  },
  uploadSubText: {
    color: theme.palette.text.secondary,
    fontSize: '13px',
    fontFamily: 'Inter',
    marginTop: '6px',
  },
  fileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxHeight: '220px',
    overflowY: 'auto',
  },
  fileItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '10px',
    padding: '10px 12px',
    gap: '12px',
  },
  fileInfo: {
    minWidth: 0,
    flex: 1,
  },
  fileName: {
    color: theme.palette.common.white,
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileMeta: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    fontFamily: 'Inter',
    marginTop: '2px',
  },
  hiddenInput: {
    display: 'none',
  },
}));

interface VendorDocumentUploadPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onUpload: (files: File[]) => Promise<any>;
}

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/jpg',
  'image/png',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024;

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const VendorDocumentUploadPopup = ({
  isOpen,
  onClose,
  onSuccess,
  onUpload,
}: VendorDocumentUploadPopupProps) => {
  const { classes } = useStyles();
  const { setAlert } = useSnackBar();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const resetState = () => {
    setUploadedFiles([]);
    setIsDragging(false);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateFiles = (files: File[]) => {
    const validFiles: File[] = [];

    for (const file of files) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        setAlert((prev) => ({
          ...prev,
          message: `${file.name} has an unsupported file type.`,
          type: 'error',
        }));
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        setAlert((prev) => ({
          ...prev,
          message: `${file.name} exceeds the 10MB limit.`,
          type: 'error',
        }));
        continue;
      }

      validFiles.push(file);
    }

    return validFiles;
  };

  const addFiles = (incomingFiles: FileList | File[]) => {
    const filesArray = Array.from(incomingFiles);
    const validFiles = validateFiles(filesArray);

    setUploadedFiles((prev) => {
      const merged = [...prev, ...validFiles];
      const unique = merged.filter(
        (file, index, self) =>
          index === self.findIndex(
            (f) =>
              f.name === file.name &&
              f.size === file.size &&
              f.lastModified === file.lastModified,
          ),
      );
      return unique;
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      addFiles(event.target.files);
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setUploadedFiles((prev) =>
      prev.filter(
        (file) =>
          !(
            file.name === fileToRemove.name &&
            file.size === fileToRemove.size &&
            file.lastModified === fileToRemove.lastModified
          ),
      ),
    );
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    if (event.dataTransfer.files?.length) {
      addFiles(event.dataTransfer.files);
    }
  };

  const onSubmit = () => {
    if (!uploadedFiles.length) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please upload at least one file.',
        type: 'error',
      }));
      return;
    }

    setIsFormSubmitting(true);

    onUpload(uploadedFiles)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Documents uploaded successfully.',
          type: 'success',
        }));
        resetState();
        onSuccess?.();
        onClose();
      })
      .catch((error: AxiosError<ErrorData>) => {
        const errorMessage = error.response?.data?.message || 'Failed to upload documents.';
        setAlert((prev) => ({
          ...prev,
          message: errorMessage,
          type: 'error',
        }));
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  return (
    <Dialog className={classes.popupContainer} open={isOpen} onClose={handleClose} fullWidth>
      <Box display="flex" justifyContent="flex-end">
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disableRipple
          sx={(theme) => ({
            color: theme.palette.common.white,
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogTitle>Upload Documents</DialogTitle>

      <DialogContent>
        <Box className={classes.formContainer}>
          <Typography className={classes.headline}>Upload Files</Typography>

          <input
            ref={inputRef}
            id="vendor-document-file-input"
            type="file"
            multiple
            className={classes.hiddenInput}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleInputChange}
          />

          <Box
            className={classes.uploadBox}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setIsDragging(false);
            }}
            onDrop={handleDrop}
            sx={{
              borderColor: isDragging ? 'info.main' : 'secondary.main',
              background: isDragging ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)',
            }}
          >
            <UploadFileIcon className={classes.uploadIcon} />
            <Typography className={classes.uploadText}>
              Click to upload or drag and drop
            </Typography>
            <Typography className={classes.uploadSubText}>
              PDF, DOC, DOCX, JPG, JPEG, PNG - Max 10MB each
            </Typography>
          </Box>

          {!!uploadedFiles.length && (
            <Box className={classes.fileList}>
              {uploadedFiles.map((file) => (
                <Box
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className={classes.fileItem}
                >
                  <Box className={classes.fileInfo}>
                    <Typography className={classes.fileName}>{file.name}</Typography>
                    <Typography className={classes.fileMeta}>
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={() => handleRemoveFile(file)}
                    sx={{ color: 'white' }}
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <DialogActions className={classes.popupActions}>
            <Box maxWidth="200px" width="100%">
              <FormSubmitButton
                disabled={!uploadedFiles.length || isFormSubmitting}
                buttonIcon={<SaveIcon />}
                buttonText={isFormSubmitting ? 'Uploading...' : 'Upload'}
                onClick={onSubmit}
              />
            </Box>

            <Box maxWidth="200px" width="100%">
              <FormCancelButton
                disabled={isFormSubmitting}
                onClick={handleClose}
              />
            </Box>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VendorDocumentUploadPopup;